/**
 * Bioband Maturity Assessment Utilities
 *
 * Contains calculations and recommendations for youth player maturity assessment
 * Based on Khamis-Roche and Mirwald methods
 */

// ============================================================================
// POSITION MAPPING
// ============================================================================

/**
 * Maps specific positions to general position categories
 */
export const positionToGeneral = {
  // Goalkeeper
  GK: "Goalkeeper",
  Goalkeeper: "Goalkeeper",

  // Defenders
  CB: "Defender",
  LB: "Defender",
  RB: "Defender",
  LCB: "Defender",
  RCB: "Defender",
  LWB: "Defender",
  RWB: "Defender",
  Defender: "Defender",
  Back: "Defender",

  // Midfielders
  DM: "Midfielder",
  CDM: "Midfielder",
  CM: "Midfielder",
  AM: "Midfielder",
  CAM: "Midfielder",
  LM: "Midfielder",
  RM: "Midfielder",
  Midfielder: "Midfielder",

  // Forwards
  ST: "Forward",
  CF: "Forward",
  SS: "Forward",
  LW: "Forward",
  RW: "Forward",
  Winger: "Forward",
  Forward: "Forward",
  Attacker: "Forward",
};

/**
 * Get general position from specific position
 * @param {string} position - Specific position code
 * @returns {string} - General position category
 */
export function getGeneralPosition(position) {
  return positionToGeneral[position] || "Unknown";
}

// ============================================================================
// MATURITY CALCULATIONS
// ============================================================================

/**
 * Calculate maturity offset using Mirwald equation
 * Predicts years from Peak Height Velocity (PHV)
 *
 * @param {Object} params - Player measurements
 * @param {string} params.gender - 'M' or 'F'
 * @param {number} params.decimalAge - Age in decimal years
 * @param {number} params.height - Standing height in cm
 * @param {number} params.sittingHeight - Sitting height in cm
 * @param {number} params.weight - Weight in kg
 * @returns {number|null} - Maturity offset in years (negative = pre-PHV, positive = post-PHV)
 */
export function calculateMaturityOffset({
  gender,
  decimalAge,
  height,
  sittingHeight,
  weight,
}) {
  if (!sittingHeight || sittingHeight <= 0) {
    return null; // Sitting height required for Mirwald calculation
  }

  const legLength = height - sittingHeight;
  const weightHeightRatio = weight / height;

  let maturityOffset;

  if (gender === "M") {
    // Mirwald equation for males
    maturityOffset =
      -9.236 +
      0.0002708 * (legLength * sittingHeight) -
      0.001663 * (decimalAge * legLength) +
      0.007216 * (decimalAge * sittingHeight) +
      0.02292 * (weightHeightRatio * 100);
  } else {
    // Mirwald equation for females
    maturityOffset =
      -9.376 +
      0.0001882 * (legLength * sittingHeight) +
      0.0022 * (decimalAge * legLength) +
      0.005841 * (decimalAge * sittingHeight) -
      0.002658 * (decimalAge * weight) +
      0.07693 * (weightHeightRatio * 100);
  }

  return Math.round(maturityOffset * 100) / 100;
}

/**
 * Determine maturity category from offset
 * @param {number} maturityOffset - Years from PHV
 * @returns {string} - Maturity category
 */
export function getMaturityCategory(maturityOffset) {
  if (maturityOffset === null || maturityOffset === undefined) {
    return null;
  }

  if (maturityOffset < -1) {
    return "Pre-PHV";
  } else if (maturityOffset >= -1 && maturityOffset <= 1) {
    return "Circa-PHV";
  } else {
    return "Post-PHV";
  }
}

/**
 * Determine maturity category from %PAH (if sitting height not available)
 * @param {number} percentPAH - Percent of Predicted Adult Height
 * @returns {string} - Maturity category
 */
export function getMaturityCategoryFromPAH(percentPAH) {
  if (!percentPAH) return null;

  if (percentPAH < 85) {
    return "Pre-PHV";
  } else if (percentPAH >= 85 && percentPAH < 95) {
    return "Circa-PHV"; // Approximate - during growth spurt
  } else {
    return "Post-PHV";
  }
}

// ============================================================================
// POSITION-BASED RECOMMENDATIONS
// ============================================================================

/**
 * Complete recommendations database
 * Organized by general position and maturity category
 */
export const maturityRecommendations = {
  Goalkeeper: {
    "Pre-PHV": {
      focusAreas: ["Footwork and agility", "Distribution technique", "Decision-making", "Positioning basics", "Communication"],
      avoidAreas: ["High diving volume", "Repetitive hard surfaces"],
      loadGuidelines: "Emphasize technique over volume. Short, varied sessions. Soft surface training preferred.",
      injuryRisks: ["Wrist strains from improper technique"],
      specificNotes: {
        GK: "Focus on fundamental handling and footwork patterns",
      },
    },
    "Circa-PHV": {
      focusAreas: ["Core stability", "Low-intensity diving technique", "Reading the game", "Distribution under pressure"],
      avoidAreas: ["Repetitive diving drills", "Heavy crossing sessions", "Hard surface diving"],
      loadGuidelines: "Reduce diving volume by 30-40%. Focus on positioning over reactions. Regular growth monitoring.",
      injuryRisks: ["Osgood-Schlatter", "Sever's disease", "Back pain from rapid growth"],
      specificNotes: {
        GK: "Monitor growth spurt effects on coordination. May need temporary technique adjustments.",
      },
    },
    "Post-PHV": {
      focusAreas: ["Shot-stopping intensity", "Cross collection", "Physical duels", "Command of area", "Advanced distribution"],
      avoidAreas: [],
      loadGuidelines: 'Full training loads appropriate. Emphasize recovery between high-intensity sessions.',
      injuryRisks: ['Shoulder injuries from diving', 'Knee impact injuries'],
      specificNotes: {
        'GK': 'Can now handle full training loads. Focus on match-specific scenarios.'
      }
    }
  },
  
  Defender: {
    'Pre-PHV': {
      focusAreas: ['Passing range', '1v1 defending technique', 'Positional awareness', 'Ball control under pressure', 'Reading the game'],
      avoidAreas: ['Excessive heading practice', 'High-volume physical duels'],
      loadGuidelines: 'Technical focus 70%, physical 30%. Use modified heading protocols. Encourage intelligent positioning.',
      injuryRisks: ['Ankle sprains from turning', 'Minor muscle strains'],
      specificNotes: {
        'CB': 'Emphasize positioning and anticipation over physicality',
        'LB': 'Focus on overlapping timing and delivery technique',
        'RB': 'Focus on overlapping timing and delivery technique'
      }
    },
    'Circa-PHV': {
      focusAreas: ['Aerial timing (not volume)', 'Recovery running technique', 'Communication', 'Tactical understanding'],
      avoidAreas: ['Excessive heading', 'High collision training', 'Repeated jumping/landing'],
      loadGuidelines: 'Reduce heading by 50%. Limit high-intensity physical sessions. Focus on game understanding.',
      injuryRisks: ['Osgood-Schlatter', 'Sever\'s disease', 'Growth plate injuries', 'Back pain'],
      specificNotes: {
        'CB': 'Reduce aerial duel volume. Focus on timing and positioning.',
        'LB': 'Monitor overlap sprint loads',
        'RB': 'Monitor overlap sprint loads'
      }
    },
    'Post-PHV': {
      focusAreas: ['Aerial duels', 'Physical defending', 'Leadership', 'High-intensity recovery runs', 'Press resistance'],
      avoidAreas: [],
      loadGuidelines: 'Full physical training loads. Ensure adequate recovery between matches.',
      injuryRisks: ['Hamstring strains', 'Groin injuries from sliding'],
      specificNotes: {
        'CB': 'Full aerial training appropriate. Develop dominance.',
        'LB': 'Increase overlap frequency and intensity',
        'RB': 'Increase overlap frequency and intensity'
      }
    }
  },
  
  Midfielder: {
    'Pre-PHV': {
      focusAreas: ['First touch', 'Passing variety', 'Spatial scanning', 'Movement off ball', 'Basic pressing triggers'],
      avoidAreas: ['High-intensity box-to-box demands', 'Excessive running loads'],
      loadGuidelines: 'Technical sessions prioritized. Limited high-intensity running. Varied playing positions encouraged.',
      injuryRisks: ['Overuse injuries from volume', 'Ankle injuries'],
      specificNotes: {
        'DM': 'Emphasize screening angles and distribution',
        'CM': 'Focus on receiving in tight spaces',
        'AM': 'Develop creativity and vision'
      }
    },
    'Circa-PHV': {
      focusAreas: ['Game intelligence', 'Movement patterns', 'Tactical positioning', 'Decision-making speed'],
      avoidAreas: ['High-intensity box-to-box demands', 'Excessive sprint training'],
      loadGuidelines: 'Reduce running loads by 20-30%. Increase tactical/positional work. Monitor closely for pain.',
      injuryRisks: ['Osgood-Schlatter', 'Sever\'s disease', 'Stress fractures', 'Hip flexor issues'],
      specificNotes: {
        'DM': 'Focus on positioning over physical battles',
        'CM': 'Reduce running loads, increase tactical work',
        'AM': 'Maintain creativity sessions, reduce intensity'
      }
    },
    'Post-PHV': {
      focusAreas: ['Pressing intensity', 'Transition speed', 'Physical duels', 'Full 90-minute demands', 'Leadership'],
      avoidAreas: [],
      loadGuidelines: 'Full training loads appropriate. Periodization important for match congestion.',
      injuryRisks: ['Hamstring strains', 'Groin injuries', 'Fatigue-related soft tissue'],
      specificNotes: {
        'DM': 'Full physical development - tackling, heading, duels',
        'CM': 'Complete midfielder development',
        'AM': 'Add physical elements to creative play'
      }
    }
  },
  
  Forward: {
    'Pre-PHV': {
      focusAreas: ['Movement patterns', 'Finishing technique', '1v1 dribbling', 'Link-up play', 'Pressing triggers'],
      avoidAreas: ['Excessive sprint loading', 'Heavy strength work'],
      loadGuidelines: 'Technical finishing prioritized. Quality over quantity in sprint work. Encourage creativity.',
      injuryRisks: ['Ankle sprains from dribbling', 'Minor muscle strains'],
      specificNotes: {
        'ST': 'Movement in the box, timing of runs',
        'LW': '1v1 isolations, crossing technique',
        'RW': '1v1 isolations, crossing technique'
      }
    },
    'Circa-PHV': {
      focusAreas: ['Link-up play', 'Intelligent running', 'Finishing composure', 'Tactical pressing'],
      avoidAreas: ['Excessive sprint loading', 'High-volume finishing sessions'],
      loadGuidelines: 'Reduce sprint volume by 25-30%. Quality finishing sessions. Monitor for overuse.',
      injuryRisks: ['Osgood-Schlatter', 'Sever\'s disease', 'Quadriceps strains', 'Hip issues'],
      specificNotes: {
        'ST': 'Focus on movement intelligence over physical battles',
        'LW': 'Reduce sprint loads, maintain technical 1v1s',
        'RW': 'Reduce sprint loads, maintain technical 1v1s'
      }
    },
    'Post-PHV': {
      focusAreas: ['Physical hold-up play', 'Pressing from front', 'Aerial threat', 'High-intensity running', 'Strength in duels'],
      avoidAreas: [],
      loadGuidelines: 'Full training loads. Ensure sprint training is periodized. Recovery crucial for explosive players.',
      injuryRisks: ['Hamstring strains from sprinting', 'Groin injuries', 'Ankle injuries'],
      specificNotes: {
        'ST': 'Full physical development - hold up, aerial, pressing',
        'LW': 'Add physical elements to pace and skill',
        'RW': 'Add physical elements to pace and skill'
      }
    }
  }
};

/**
 * Get recommendations for a player
 * @param {string} position - Player's specific position
 * @param {string} maturityCategory - 'Pre-PHV', 'Circa-PHV', or 'Post-PHV'
 * @returns {Object|null} - Recommendations object
 */
export function getRecommendations(position, maturityCategory) {
  if (!position || !maturityCategory) return null;
  
  const generalPosition = getGeneralPosition(position);
  if (generalPosition === 'Unknown') return null;
  
  const positionRecs = maturityRecommendations[generalPosition];
  if (!positionRecs) return null;
  
  const categoryRecs = positionRecs[maturityCategory];
  if (!categoryRecs) return null;
  
  // Get specific position note if available
  const specificNote = categoryRecs.specificNotes?.[position] || null;
  
  return {
    ...categoryRecs,
    specificNote,
    generalPosition,
    maturityCategory
  };
}

// ============================================================================
// INJURY RISK ASSESSMENT
// ============================================================================

/**
 * Assess injury risk level
 * @param {Object} player - Player data
 * @returns {Object} - Risk assessment
 */
export function assessInjuryRisk(player) {
  const risks = [];
  let riskLevel = 'Low';
  
  // Check if in Circa-PHV (highest risk period)
  if (player.maturityCategory === 'Circa-PHV') {
    risks.push({
      type: 'Growth Spurt',
      severity: 'High',
      description: 'Player is in peak growth velocity phase. Increased risk of growth-related injuries.'
    });
    riskLevel = 'High';
  }
  
  // Check growth velocity
  if (player.growthVelocity && player.growthVelocity > 6) {
    risks.push({
      type: 'Rapid Growth',
      severity: 'High',
      description: `Growth rate of ${player.growthVelocity} cm/year exceeds 6cm/year threshold.`
    });
    riskLevel = 'High';
  }
  
  // Check for specific position-related risks
  const generalPosition = getGeneralPosition(player.position);
  const recs = getRecommendations(player.position, player.maturityCategory);
  if (recs?.injuryRisks) {
    recs.injuryRisks.forEach(injury => {
      risks.push({
        type: injury,
        severity: player.maturityCategory === 'Circa-PHV' ? 'Medium' : 'Low',
        description: `Common risk for ${generalPosition} at ${player.maturityCategory} stage.`
      });
    });
    if (risks.length > 2 && riskLevel !== 'High') {
      riskLevel = 'Medium';
    }
  }
  
  return {
    riskLevel,
    risks,
    requiresMonitoring: riskLevel === 'High' || risks.length > 0,
    recommendations: recs?.avoidAreas || []
  };
}

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

/**
 * Get color class for maturity category
 * @param {string} category - Maturity category
 * @returns {string} - CSS class name
 */
export function getMaturityCategoryColor(category) {
  switch (category) {
    case 'Pre-PHV':
      return 'maturity-pre-phv'; // Blue
    case 'Circa-PHV':
      return 'maturity-circa-phv'; // Orange/Yellow (warning)
    case 'Post-PHV':
      return 'maturity-post-phv'; // Green
    default:
      return 'maturity-unknown'; // Gray
  }
}

/**
 * Get badge variant for maturity category (Bootstrap)
 * @param {string} category - Maturity category
 * @returns {string} - Bootstrap variant
 */
export function getMaturityBadgeVariant(category) {
  switch (category) {
    case 'Pre-PHV':
      return 'info';
    case 'Circa-PHV':
      return 'warning';
    case 'Post-PHV':
      return 'success';
    default:
      return 'secondary';
  }
}

/**
 * Format maturity offset for display
 * @param {number} offset - Years from PHV
 * @returns {string} - Formatted string
 */
export function formatMaturityOffset(offset) {
  if (offset === null || offset === undefined) return 'N/A';
  
  const absOffset = Math.abs(offset);
  const unit = absOffset === 1 ? 'year' : 'years';
  
  if (offset < 0) {
    return `${absOffset} ${unit} to PHV`;
  } else if (offset === 0) {
    return 'At PHV';
  } else {
    return `${absOffset} ${unit} post-PHV`;
  }
}

/**
 * Format growth velocity for display
 * @param {number} velocity - cm/year
 * @returns {string} - Formatted string with warning if high
 */
export function formatGrowthVelocity(velocity) {
  if (velocity === null || velocity === undefined) return 'N/A';
  
  const formatted = `${velocity.toFixed(1)} cm/year`;
  if (velocity > 6) {
    return `⚠️ ${formatted}`;
  }
  return formatted;
}

export default {
  positionToGeneral,
  getGeneralPosition,
  calculateMaturityOffset,
  getMaturityCategory,
  getMaturityCategoryFromPAH,
  maturityRecommendations,
  getRecommendations,
  assessInjuryRisk,
  getMaturityCategoryColor,
  getMaturityBadgeVariant,
  formatMaturityOffset,
  formatGrowthVelocity
};
